import React, { forwardRef, useCallback, useEffect, useMemo, useState } from "react";

import {
    ExplicitValidation,
    IExplicitValidationFnDescription,
    IInstantValidationFnDescription,
    InstantValidation,
} from "@multiweb/reactControls";
import { bridge } from "@multiweb/reactCore/bridge";
import { combineRefs, invariant, TOOLTIP_ID_PREFIX } from "@multiweb/reactUtils";
import { moneyNumberTypes, realNumberTypes } from "@multiweb/reactUtils/shared";
import { OWDataType } from "@multiweb/types/contracts";
import { ForwardRefType, TextAlign, TextDirectionality } from "@multiweb/types/react";
import { ControlValueWrapper } from "../ControlValueWrapper";
import { EditComponent } from "../EditComponent";
import { FormComponentPropsWrapper } from "../FormComponentPropsWrapper";
import { InputFake } from "../InputFake";
import { MultiEditWrapper, MultiEditWrapperFakeComponentRendererProps } from "../MultiEditWrapper";
import { NumberFormatter } from "../NumberFormatter";
import { ControlValue, EditableControlComponentProps, FormComponentProps, NumEditComponentProps } from "../types";
import { ValidationErrorWrapper } from "../ValidationErrorWrapper";
import {
    addDecimalSeparator,
    remapNumbers,
    removeGroupSeparator,
    removeNumberFormatting,
    useDataTypeSpecificProps,
} from "./utils";

const {
    createBigNumberProxy,
    getFormattedNumberString,
    getNumberFormat,
} = bridge;

/** Display format pro 2 desetinná místa. */
const DISPLAY_FORMAT_TWO_DECIMALS = "0.00";

export type NumEditProps = Omit<EditableControlComponentProps, "ref">
    & NumEditComponentProps
    & Pick<FormComponentProps, "transparent">;

export const NumEdit: ForwardRefType<HTMLInputElement, NumEditProps> = forwardRef<HTMLInputElement, NumEditProps>(({
    id,
    dataKey,
    displayFormat: displayFormatOriginal,
    dataType,
    onMount,
    onValidationCallback,
    postponedRequired,
    required,
    recommended,
    enabledVisual,
    triggerValueChange,
    externalErrors,
    isInRequiredInvalidState,
    encrypted,
    onMouseLeave,
    onMouseEnter,
    onMouseOver,
    onBlur,
    onFocus,
    transparent,
    onValidityChanged,
    onChange: onChangeExternal,
    minValue: minValueOriginal,
    maxValue: maxValueOriginal,
    locKey,
    isEditing,
    touched,
    multiEditActive,
    onKeyDown,
    onClick,
}, ref) => {
    // Příznak, zda uživatel změnil hodnotu
    const [isTouched, setIsTouched] = useState(false);
    const [isFocused, setIsFocused] = useState(false);

    const { minValue, maxValue, displayFormat, isBigNumber, isWholeNumber } = useDataTypeSpecificProps({
        minValue: minValueOriginal,
        maxValue: maxValueOriginal,
        dataType,
        displayFormat: displayFormatOriginal,
    });
    const { numbers, useArabicNumbers } = getNumberFormat();

    const numberCharactersOnlyDescription: IInstantValidationFnDescription = useMemo(() => (
        InstantValidation.createNumberCharactersOnlyDescription(isWholeNumber)
    ), [isWholeNumber]);

    const minValueDescription: IExplicitValidationFnDescription | null = useMemo(() => (
        minValue ? ExplicitValidation.createMinValueDescription(minValue, isBigNumber) : null
    ), [isBigNumber, minValue]);

    const maxValueDescription: IExplicitValidationFnDescription | null = useMemo(() => (
        maxValue ? ExplicitValidation.createMaxValueDescription(maxValue, isBigNumber) : null
    ), [isBigNumber, maxValue]);

    const instantValidationFnDescriptions: IInstantValidationFnDescription[] = useMemo(() => (
        [numberCharactersOnlyDescription]
    ), [numberCharactersOnlyDescription]);

    const explicitValidationFnDescriptions: IExplicitValidationFnDescription[] = useMemo(() => {
        const descriptions: IExplicitValidationFnDescription[] = [];
        [minValueDescription, maxValueDescription].forEach(description => description && descriptions.push(description));
        return descriptions;
    }, [minValueDescription, maxValueDescription]);

    useEffect(() => {
        onMount && onMount({
            getDisplayedValue: (rawValue) => rawValue,
            /**
             * Zpracuje hodnotu uloženou v reduxu a převede ji do podoby kompatibilní s Multiwebem.
             * @param rawValue Hodnota v reduxu.
             */
            getControlValue: (rawValue) => {
                if (!rawValue || Number.isNaN(+rawValue))
                    return "";

                // Hodnota typu decimal nebo money se odešle na službu zaokrouhlená, viz 29790
                if (dataType && (moneyNumberTypes.has(dataType as OWDataType) || realNumberTypes.has(dataType as OWDataType))) {
                    const valueToFormat = isBigNumber ? createBigNumberProxy(rawValue) : rawValue;
                    // Naformátuje se na dvě desetinná místa a odstraní specifické oddělovače
                    const formattedValue = getFormattedNumberString(valueToFormat, { displayFormat: displayFormat ?? DISPLAY_FORMAT_TWO_DECIMALS, dataType });
                    const cleanedValue = useArabicNumbers ? formattedValue : remapNumbers(formattedValue, numbers);
                    return removeNumberFormatting(cleanedValue);
                }

                return rawValue;
            },
            /**
             * Převede hodnotu ze záznamu na hodnotu kterou lze uložit v reduxu.
             * Hodnota BigNumber se v reduxu uloží jako řetězec. Zároveň se nastaví příznak {@link isBigNumber},
             * podle kterého NumEdit předá hodnotu formatteru jako BigNumber nebo jako klasický řetězec.
             *
             * @param controlValue Hodnota ze záznamu.
             * @returns string Výsledná hodnota je řetězec.
             */
            convertControlValueToRawValue: (controlValue: ControlValue): string => {
                if (controlValue == null) {
                    return "";
                }

                if (typeof controlValue === "number") {
                    const formattedValue = getFormattedNumberString(controlValue, { displayFormat, dataType });
                    return removeNumberFormatting(formattedValue);
                }

                // Případ, že hodnota je BigNumber.
                if (typeof controlValue === "object") {
                    invariant(controlValue.toString, "[NumEdit] Object is not a BigNumber.");
                    return controlValue.toString();
                }

                return removeNumberFormatting(controlValue);
            },
        });
    }, [onMount, dataType, displayFormat, isBigNumber, useArabicNumbers, numbers]);

    const fakeComponentRenderer = useCallback(({ disabled }: MultiEditWrapperFakeComponentRendererProps) => (
        <InputFake { ...{ disabled, isEditing, locKey, onKeyDown, onClick, required, recommended, postponedRequired } } />
    ), [isEditing, locKey, onKeyDown, onClick, required, recommended, postponedRequired]);

    const tooltipId = TOOLTIP_ID_PREFIX + dataKey;

    return (
        <ControlValueWrapper dataKey={dataKey}>
            {({ rawValue, onChange }) => (
                <ValidationErrorWrapper
                    {...{
                        dataKey,
                        tooltipId,
                        externalErrors,
                        isInRequiredInvalidState,
                        onValidityChanged,
                        instantValidationFnDescriptions,
                        explicitValidationFnDescriptions,
                    }}
                >
                    {({ validate, invalid }) => (
                        <FormComponentPropsWrapper {...{ postponedRequired, required, recommended, enabledVisual }}>
                            {({ disabled, backgroundColor, readOnly }) => (
                                <MultiEditWrapper fakeComponentRenderer={fakeComponentRenderer} {...{ multiEditActive, disabled, isEditing, touched }}>
                                    {({ ref: checkBoxRef }) => {
                                        const joinedRefs = combineRefs<HTMLInputElement>(checkBoxRef, ref);
                                        const isSign = rawValue === "+" || rawValue === "-";
                                        const valueToFormat = isBigNumber && !isSign ? createBigNumberProxy(rawValue) : rawValue;

                                        return (
                                            <NumberFormatter
                                                value={valueToFormat}
                                                {...{ displayFormat, dataType }}
                                            >
                                                {(formattedValue) => {
                                                    // Pokud uživatel píše, viditelná hodnota není formátována (docházelo by k zaokrouhlování a ořezávání řetězce)
                                                    let value = isTouched && useArabicNumbers ? addDecimalSeparator(rawValue) : formattedValue;
                                                    value = isFocused && !readOnly && isEditing ? removeGroupSeparator(value) : value;

                                                    const valueChangeTrigger = () => {
                                                        const cleanedValue = useArabicNumbers ? formattedValue : remapNumbers(formattedValue, numbers);
                                                        const cultureNeutralValue = removeNumberFormatting(cleanedValue);
                                                        /*
                                                        Po opuštění pole se naformátovaná hodnota vyčistí a uloží do reduxu
                                                           - děje se tak kvůli možnému zaokrouhlení hodnoty ve formátovači, kvůli kterému neprochází explicitní validace.
                                                         */
                                                        onChange(cultureNeutralValue);
                                                        triggerValueChange && triggerValueChange();
                                                    };

                                                    return (
                                                        <EditComponent
                                                            ref={joinedRefs}
                                                            onFocus={(event) => {
                                                                setIsFocused(true);
                                                                onFocus && onFocus(event);
                                                            }}
                                                            onBlur={(event) => {
                                                                if (isTouched) {
                                                                    valueChangeTrigger();
                                                                }

                                                                setIsFocused(false);
                                                                setIsTouched(false);
                                                                validate(onValidationCallback);
                                                                onBlur && onBlur(event);
                                                            }}
                                                            onChange={(value = "") => {
                                                                setIsTouched(true);
                                                                const cleanedValue = useArabicNumbers ? value : remapNumbers(value, numbers);
                                                                const cultureNeutralValue = removeNumberFormatting(cleanedValue);
                                                                onChange(cultureNeutralValue);
                                                                onChangeExternal && onChangeExternal(value);
                                                            }}
                                                            onJavaScriptChange={(_e) => {
                                                                valueChangeTrigger();
                                                            }}
                                                            dir={useArabicNumbers ? TextDirectionality.ltr : TextDirectionality.rtl}
                                                            textAlign={TextAlign.end}
                                                            ariaDescribedBy={tooltipId}
                                                            fixCaretPosition={false}
                                                            {...{
                                                                id,
                                                                encrypted,
                                                                onMouseLeave,
                                                                onMouseEnter,
                                                                onMouseOver,
                                                                onClick,
                                                                backgroundColor,
                                                                readOnly,
                                                                invalid,
                                                                value,
                                                                transparent,
                                                            }}
                                                        />
                                                    );
                                                }}
                                            </NumberFormatter>
                                        );
                                    }}
                                </MultiEditWrapper>
                            )}
                        </FormComponentPropsWrapper>
                    )}
                </ValidationErrorWrapper>
            )}
        </ControlValueWrapper>
    );
});
